/**
 * GitHub's blockquote alerts: a quote whose first line is `[!NOTE]` — or TIP, IMPORTANT,
 * WARNING, CAUTION — renders as a titled callout. They are GitHub's own extension rather than
 * GFM, so remark-gfm leaves the marker as literal text in the quote. This lifts the marker off
 * the mdast into a `data-alert` attribute for the blockquote renderer to style, and removes
 * the marker line itself.
 *
 * Only a marker with nothing after it on its own line counts, which is GitHub's rule:
 * `> [!NOTE] aside` is an ordinary quote.
 */

interface MarkdownAstNode {
  type?: string;
  value?: unknown;
  data?: {
    hProperties?: Record<string, unknown>;
  };
  children?: MarkdownAstNode[];
}

const GITHUB_ALERT_MARKER = /^\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\](?:\r?\n|$)/i;

/**
 * The first inline text node of a quote's first paragraph, or `null` when the
 * quote does not open that way. Pure shape narrowing — every branch guards
 * against an mdast node we did not expect, and none of it knows what a marker
 * is. Kept apart from the match below so neither reads as the other's business.
 */
function openingTextOfQuote(
  node: MarkdownAstNode,
): { paragraph: MarkdownAstNode; text: MarkdownAstNode } | null {
  if (node.type !== "blockquote") return null;
  const paragraph = node.children?.[0];
  if (!paragraph || paragraph.type !== "paragraph") return null;
  const text = paragraph.children?.[0];
  if (!text || text.type !== "text") return null;
  return typeof text.value === "string" ? { paragraph, text } : null;
}

/** The marker site, once the quote is known to open with plain text. */
function readMarkerSite(
  node: MarkdownAstNode,
): { paragraph: MarkdownAstNode; text: MarkdownAstNode; match: RegExpExecArray } | null {
  const opening = openingTextOfQuote(node);
  if (!opening) return null;
  const match = GITHUB_ALERT_MARKER.exec(String(opening.text.value));
  return match?.[1] ? { ...opening, match } : null;
}

/**
 * Removes the marker line, returning false when the marker turns out to share
 * its line with other content — which GitHub does not read as an alert.
 */
function stripMarkerLine(
  node: MarkdownAstNode,
  paragraph: MarkdownAstNode,
  text: MarkdownAstNode,
  match: RegExpExecArray,
): boolean {
  const remainder = String(text.value).slice(match[0].length);
  // Whether the marker's own line ended inside this text node. Without it, an empty remainder
  // is ambiguous: `[!NOTE]\n**bold**` parses as [text "[!NOTE]\n", strong] — next-line content
  // in a sibling — while `[!NOTE]*aside*` parses the same way minus the newline.
  const markerEndsItsLine = match[0].endsWith("\n");

  if (remainder.length > 0) {
    // The quote continues on the next line inside the same text node: keep the paragraph,
    // shed the marker line.
    text.value = remainder;
    return true;
  }
  if (markerEndsItsLine || paragraph.children?.length === 1) {
    // The marker was the whole text node — a marker-only paragraph, or a marker line whose
    // next line starts as a sibling inline. Drop the node, and the paragraph if it empties.
    paragraph.children?.shift();
    if (paragraph.children?.length === 0) node.children?.shift();
    return true;
  }
  // Something else shares the marker's line — `[!NOTE]*aside*`.
  return false;
}

function readGithubAlert(node: MarkdownAstNode): void {
  const site = readMarkerSite(node);
  if (!site) return;
  if (!stripMarkerLine(node, site.paragraph, site.text, site.match)) return;

  node.data = {
    ...node.data,
    hProperties: {
      ...node.data?.hProperties,
      dataAlert: String(site.match[1]).toLowerCase(),
    },
  };
}

export function remarkGithubAlerts() {
  return (tree: MarkdownAstNode) => {
    const visit = (node: MarkdownAstNode) => {
      node.children?.forEach(visit);
      readGithubAlert(node);
    };
    visit(tree);
  };
}
